// Celebratory piano flourish played on successful puzzle completion.
//
// We synthesize the riff at runtime with the Web Audio API rather than
// shipping an MP3 to keep the bundle slim and avoid the "audio asset
// blocked by autoplay" headaches some browsers cause. The riff is a
// short C-major chord arpeggio + a top C — about 1.4 seconds total —
// using a simple additive synthesis approach (fundamental + a few
// harmonics with an exponential decay envelope) that lands somewhere
// between a digital piano and a music box. Surprisingly nice.
//
// Honors the `soundEffects` user preference. Safe to call repeatedly
// (each call constructs a fresh AudioContext if needed).

let _ctx = null;

function getCtx() {
  if (typeof window === 'undefined') return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!_ctx) _ctx = new AC();
  // Browsers suspend the context until a user gesture. Most callers
  // here are firing from a click/keystroke chain so this resume() is
  // already-fulfilled most of the time.
  if (_ctx.state === 'suspended') _ctx.resume().catch(() => {});
  return _ctx;
}

// Play a single piano-ish note at `freq` Hz, starting at `startTime`
// (audio-clock seconds), with given duration and peak gain.
function playNote(ctx, freq, startTime, duration, peakGain) {
  // Fundamental + 2nd harmonic + 3rd harmonic at decreasing amplitudes.
  // Triangle waves give a hollow, bell-like attack that resembles a
  // sampled piano better than pure sines.
  const partials = [
    { wave: 'triangle', mult: 1, gain: 1.0 },
    { wave: 'sine', mult: 2, gain: 0.35 },
    { wave: 'sine', mult: 3, gain: 0.12 },
  ];
  const noteGain = ctx.createGain();
  noteGain.gain.setValueAtTime(0, startTime);
  noteGain.gain.linearRampToValueAtTime(peakGain, startTime + 0.01);
  noteGain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);
  noteGain.connect(ctx.destination);

  partials.forEach((p) => {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    g.gain.value = p.gain;
    osc.type = p.wave;
    osc.frequency.value = freq * p.mult;
    osc.connect(g);
    g.connect(noteGain);
    osc.start(startTime);
    osc.stop(startTime + duration + 0.05);
  });
}

// Notes (C major, octaves 4-6) and their frequencies in Hz.
const NOTES = {
  C4: 261.63, E4: 329.63, G4: 392.0,
  C5: 523.25, E5: 659.25, G5: 783.99,
  C6: 1046.5,
};

// Plays a short ascending C-major flourish followed by a chord stab.
// ~1.4s total.
export function playVictoryRiff(volume = 0.18) {
  const ctx = getCtx();
  if (!ctx) return;
  const now = ctx.currentTime + 0.02; // tiny lead-in
  // Ascending arpeggio: C4 E4 G4 C5 E5 G5 C6 — 60ms spacing
  const arp = ['C4', 'E4', 'G4', 'C5', 'E5', 'G5', 'C6'];
  arp.forEach((n, i) => {
    playNote(ctx, NOTES[n], now + i * 0.07, 0.6, volume);
  });
  // Final chord stab on top of the last note for body
  const chordStart = now + arp.length * 0.07 + 0.05;
  ['C5', 'E5', 'G5', 'C6'].forEach((n) => {
    playNote(ctx, NOTES[n], chordStart, 1.1, volume * 0.75);
  });
}

// Lightweight "primer" call — invoke this on any user gesture (e.g.
// the first puzzle click) to unlock the AudioContext on iOS/Safari
// so the victory riff plays without delay at the end of the game.
export function primeAudio() {
  getCtx();
}
